import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  TextField,
  Breadcrumbs,
  Link,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
  LinearProgress,
  InputAdornment,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Slideshow as SlideshowIcon,
  DragIndicator as DragIndicatorIcon,
  AutoAwesome as AutoAwesomeIcon,
  Storefront as StorefrontIcon,
  Image as ImageIcon,
  Sync as SyncIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import noImage from '../assets/no-image.png';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getQuotation,
  addQuotationLineItem,
  updateQuotationLineItem,
  removeQuotationLineItem,
  searchProducts,
  saveQuotation,
  getAllPriceHistoryForSku,
  getExportColumns,
  quotationExportUrl,
  getPptxFields,
  exportQuotationPptx,
  refreshQuotationLineItemPrice,
  refreshQuotationPrices,
} from './quotationApi.js';

const EXPORT_FORMAT_LABELS = { excel: 'Excel', pdf: 'PDF', pptx: 'PowerPoint' };

// Cycled through while the PPTX build request is in flight - purely
// cosmetic (the real work happens server-side in one request), but gives
// visible sense of progress instead of a bare spinner for what can be a
// several-second AI + slide-building call.
const PPTX_BUILD_STEPS = [
  'Analyzing your selected products...',
  'Asking AI to design your theme...',
  'Building your slides...',
  'Adding the Gibson watermark...',
  'Finalizing your presentation...',
];

// Cycled through while the Sales Sheet (PDF) export is in flight - a Sales
// Sheet fetches each line's product photo/brand logo and renders a full
// page per item, which can take a while for a quotation with several
// items, so this gives visible progress instead of a bare button spinner.
const SALES_SHEET_BUILD_STEPS = [
  'Gathering your quoted items...',
  'Fetching product photos and branding...',
  'Laying out each Sales Sheet page...',
  'Adding Gibson branding...',
  'Finalizing your PDF...',
];

// Ready-made design briefs for the "Instructions for AI" field. Each names
// colors, a background tone, a font feel, and (in most cases) a layout
// style (bars/sidebar/frame/minimal - see API/quotation_exports.py) so
// clicking one demonstrates the AI varying more than just the palette.
const PPTX_PROMPT_EXAMPLES = [
  {
    label: 'Elegant & framed',
    text: 'Elegant and timeless: classic serif font, with a full frame border around each slide.',
  },
  {
    label: 'Bold & modern',
    text: 'Bold and modern: clean geometric sans-serif font, bold accent bars across the top and bottom '
      + 'of each slide.',
  },
  {
    label: 'Editorial sidebar',
    text: 'Clean editorial magazine look: a vertical accent sidebar stripe down the left edge.',
  },
  {
    label: 'Minimalist',
    text: 'Quiet minimalist look: understated design with no bars or borders, just a thin accent line and '
      + 'elegant serif font.',
  },
  {
    label: 'Festive & seasonal',
    text: 'Festive holiday catalog feel with a small seasonal snowflake accent, elegant serif font.',
  },
];

// Shared by the price-history effect and the Add Item picker: reduces a raw
// price-history response (which can have many superseded rows per category)
// to just the most recent row per category, annotated with whether that
// row is expired - a category's price never disappears, it just gets
// flagged, so the user can still choose it if they confirm they want to.
const reduceToLatestPerCategory = (rows) => {
  const today = new Date().toISOString().slice(0, 10);
  const latestByCategory = {};
  rows.forEach((row) => {
    const existing = latestByCategory[row.price_category];
    if (!existing || row.effective_start_date > existing.effective_start_date) {
      latestByCategory[row.price_category] = row;
    }
  });
  return Object.values(latestByCategory).map((row) => ({
    ...row,
    isExpired: Boolean(row.effective_end_date && row.effective_end_date < today),
  }));
};

const SortableColumnRow = ({ id, label, checked, onToggle, forced }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        py: 0.25,
        px: 0.5,
        borderRadius: 1,
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.disabled', mr: 0.5, touchAction: 'none' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <FormControlLabel
        control={<Checkbox size="small" checked={checked} onChange={onToggle} disabled={forced} />}
        label={forced ? `${label} (always included)` : label}
        sx={{ mr: 0, flex: 1, userSelect: 'none', '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
      />
    </Box>
  );
};

const QuotationDetailPage = () => {
  const { quotationId } = useParams();
  const navigate = useNavigate();

  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [quotationName, setQuotationName] = useState('');
  const [saving, setSaving] = useState(false);
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [skuPriceOptionsMap, setSkuPriceOptionsMap] = useState({});
  const [sellingPriceDrafts, setSellingPriceDrafts] = useState({});
  const [refreshingItemId, setRefreshingItemId] = useState(null);
  const [refreshingAllPrices, setRefreshingAllPrices] = useState(false);

  // "Add Item" picker - lets a sales rep add a brand-new line directly from
  // this page, whether the quotation is a draft or already saved (saved
  // quotations previously had no way to add items at all).
  const [addItemQuery, setAddItemQuery] = useState('');
  const [addItemOptions, setAddItemOptions] = useState([]);
  const [addItemSearching, setAddItemSearching] = useState(false);
  const [addItemProduct, setAddItemProduct] = useState(null);
  const [addItemPriceOptions, setAddItemPriceOptions] = useState([]);
  const [addItemPriceCategory, setAddItemPriceCategory] = useState('');
  const [addItemQuantity, setAddItemQuantity] = useState(1);
  const [addingItem, setAddingItem] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [changingCategoryItemId, setChangingCategoryItemId] = useState(null);
  // null when closed, otherwise { kind: 'add' | 'switch', itemId?, option }
  // - gates adding a new item or switching an existing line's category to
  // one whose latest price has expired, behind an explicit confirmation.
  const [expiredPriceConfirm, setExpiredPriceConfirm] = useState(null);
  // Bumped after a successful add to force the Autocomplete to remount and
  // fully reset (selected value + its own internal input text) - see the
  // comment on onInputChange below for why inputValue isn't controlled.
  const [addItemResetKey, setAddItemResetKey] = useState(0);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [imageModalUrl, setImageModalUrl] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState(null);

  // PowerPoint has its own, larger field set (full product detail, one
  // slide per product) plus a free-text design brief - kept as separate
  // state from the Excel/PDF column picker above rather than shoehorned
  // into the same shape.
  const [availablePptxFields, setAvailablePptxFields] = useState([]);
  const [pptxFieldOrder, setPptxFieldOrder] = useState([]);
  const [selectedPptxFields, setSelectedPptxFields] = useState(new Set());
  const [pptxDesignDescription, setPptxDesignDescription] = useState('');
  // When true, skip the AI theme call entirely and use the plain white
  // Gibson-branded template (API/quotation_exports.py's DEFAULT_PPTX_THEME) -
  // for users who just want a quick, predictable deck without describing a design.
  const [pptxUseDefaultTemplate, setPptxUseDefaultTemplate] = useState(false);
  const [pptxBuildingOpen, setPptxBuildingOpen] = useState(false);
  const [pptxBuildStepIndex, setPptxBuildStepIndex] = useState(0);
  const pptxAbortControllerRef = useRef(null);
  const [salesSheetBuildingOpen, setSalesSheetBuildingOpen] = useState(false);
  const [salesSheetBuildStepIndex, setSalesSheetBuildStepIndex] = useState(0);
  const salesSheetAbortControllerRef = useRef(null);

  // Column/field choices default to "everything selected, backend order"
  // the first time they load. *Order state is the drag-reorderable
  // sequence; selected*/selectedColumns is just which of those are checked.
  useEffect(() => {
    let cancelled = false;
    getExportColumns()
      .then((columns) => {
        if (cancelled) return;
        setAvailableColumns(columns);
        setColumnOrder(columns.map((c) => c.key));
        setSelectedColumns(new Set(columns.map((c) => c.key)));
      })
      .catch(() => {
        // The export buttons still work with the backend's own "all columns"
        // default even if this list fails to load - just without the dialog.
      });
    getPptxFields()
      .then((fields) => {
        if (cancelled) return;
        setAvailablePptxFields(fields);
        setPptxFieldOrder(fields.map((f) => f.key));
        // Unlike the Excel/PDF columns above, PowerPoint fields start
        // unchecked - a full AI-designed slide with every field is
        // usually too dense; the user picks in only what they want.
        setSelectedPptxFields(new Set());
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const columnLabelByKey = {};
  availableColumns.forEach((c) => { columnLabelByKey[c.key] = c.label; });
  const pptxFieldLabelByKey = {};
  availablePptxFields.forEach((f) => { pptxFieldLabelByKey[f.key] = f.label; });

  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleColumnDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const handlePptxFieldDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPptxFieldOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const handleTogglePptxField = (key) => {
    // Unlike the Excel/PDF columns, SKU isn't forced here - PowerPoint
    // slides already carry the SKU/item number in the title area, so
    // hiding the "Item #" line is a legitimate design choice.
    setSelectedPptxFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSelectAllPptxFields = () => setSelectedPptxFields(new Set(availablePptxFields.map((f) => f.key)));
  const handleUnselectAllPptxFields = () => setSelectedPptxFields(new Set());

  const fetchQuotation = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQuotation(quotationId);
      setQuotation(data);
      setQuotationName(data.name || '');
    } catch (error) {
      setSnackbar({ open: true, message: 'Could not load this quotation.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    fetchQuotation();
  }, [fetchQuotation]);

  const isDraft = quotation?.status === 'draft';

  // Fetches each line's full price history once into `skuPriceOptionsMap`
  // (latest row per category, expired ones included and flagged via
  // isExpired/effective_end_date). Drives both the Price Category dropdowns
  // (every category offered, expired or not) and the "Price Now" comparison
  // column - which shows the latest known price even when it's expired,
  // rather than "N/A", since that's still useful to see and act on.
  useEffect(() => {
    if (!quotation || quotation.line_items.length === 0) return;
    let cancelled = false;

    const fetchPriceHistory = async () => {
      try {
        const uniqueSkus = [...new Set(quotation.line_items.map((item) => item.sku))];
        const results = await Promise.all(uniqueSkus.map((sku) => getAllPriceHistoryForSku(sku)));
        if (cancelled) return;
        const optionsMap = {};
        uniqueSkus.forEach((sku, i) => {
          optionsMap[sku] = reduceToLatestPerCategory(results[i]);
        });
        setSkuPriceOptionsMap(optionsMap);
      } catch (error) {
        // Comparison/dropdown options are a nice-to-have - failing to load
        // them shouldn't block the page; the dropdown just falls back to
        // showing only the line's current category when options are empty.
      }
    };
    fetchPriceHistory();

    return () => { cancelled = true; };
  }, [quotation?.id, quotation?.line_items?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenColumnsDialog = (format) => {
    setPendingExportFormat(format);
    setColumnsDialogOpen(true);
  };

  const handleToggleColumn = (key) => {
    if (key === 'sku') return; // always included - checkbox is disabled, this is defense in depth
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSelectAllColumns = () => setSelectedColumns(new Set(availableColumns.map((c) => c.key)));
  const handleUnselectAllColumns = () => setSelectedColumns(new Set(['sku']));

  const handleConfirmExport = () => {
    setColumnsDialogOpen(false);
    if (pendingExportFormat === 'pptx') {
      const orderedFields = pptxFieldOrder.filter((key) => selectedPptxFields.has(key));
      handlePptxExport(orderedFields, pptxUseDefaultTemplate ? '' : pptxDesignDescription, pptxUseDefaultTemplate);
    } else {
      const orderedSelection = columnOrder.filter((key) => selectedColumns.has(key));
      handleExport(pendingExportFormat, orderedSelection);
    }
  };

  const downloadBlobResponse = (response, fallbackFilename) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    link.download = match ? match[1].replace(/['"]/g, '') : fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format, columns) => {
    setExportingFormat(format);
    // The Sales Sheet (PDF) fetches each line's product photo/brand logo and
    // renders a full page per item, so it can genuinely take a while for a
    // quotation with several items - show the same kind of build animation
    // the PowerPoint export already has, instead of a bare button spinner.
    const isSalesSheet = format === 'pdf';
    let stepTimer;
    let controller;
    if (isSalesSheet) {
      setSalesSheetBuildStepIndex(0);
      setSalesSheetBuildingOpen(true);
      stepTimer = setInterval(() => {
        setSalesSheetBuildStepIndex((prev) => Math.min(prev + 1, SALES_SHEET_BUILD_STEPS.length - 1));
      }, 1800);
      controller = new AbortController();
      salesSheetAbortControllerRef.current = controller;
    }
    try {
      const token = localStorage.getItem('access');
      const response = await axios.get(quotationExportUrl(quotation.id, format, columns), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        signal: controller?.signal,
      });
      const extension = format === 'excel' ? 'xlsx' : format;
      downloadBlobResponse(response, `quotation_${quotation.id}.${extension}`);
      if (isSalesSheet) {
        setSnackbar({ open: true, message: 'Your Sales Sheet is ready.', severity: 'success' });
      }
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        setSnackbar({ open: true, message: 'Sales Sheet build cancelled.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
      }
    } finally {
      if (isSalesSheet) {
        clearInterval(stepTimer);
        salesSheetAbortControllerRef.current = null;
        setSalesSheetBuildingOpen(false);
      }
      setExportingFormat(null);
    }
  };

  const handleCancelSalesSheetBuild = () => {
    salesSheetAbortControllerRef.current?.abort();
  };

  const handlePptxExport = async (fields, designDescription, plain) => {
    setExportingFormat('pptx');
    setPptxBuildStepIndex(0);
    setPptxBuildingOpen(true);

    const stepTimer = setInterval(() => {
      setPptxBuildStepIndex((prev) => Math.min(prev + 1, PPTX_BUILD_STEPS.length - 1));
    }, 1800);

    const controller = new AbortController();
    pptxAbortControllerRef.current = controller;

    try {
      const response = await exportQuotationPptx(quotation.id, fields, designDescription, controller.signal, plain);
      downloadBlobResponse(response, `quotation_${quotation.id}.pptx`);
      setSnackbar({ open: true, message: 'Your presentation is ready.', severity: 'success' });
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        setSnackbar({ open: true, message: 'PowerPoint build cancelled.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
      }
    } finally {
      clearInterval(stepTimer);
      pptxAbortControllerRef.current = null;
      setPptxBuildingOpen(false);
      setExportingFormat(null);
    }
  };

  const handleCancelPptxBuild = () => {
    pptxAbortControllerRef.current?.abort();
  };

  const handleQuantityChange = (itemId, value) => {
    setQuantityDrafts((prev) => ({ ...prev, [itemId]: value }));
  };

  // While a quantity edit is in flight (not yet committed to the server on
  // blur), compute totals from the in-progress draft instead of the last
  // server response, so Total Amount updates instantly as you type rather
  // than waiting on the PATCH round-trip.
  const getDisplayQuantity = (item) => {
    const draft = quantityDrafts[item.id];
    if (draft === undefined) return item.quantity;
    const parsed = parseInt(draft, 10);
    return Number.isNaN(parsed) ? item.quantity : Math.max(1, parsed);
  };

  // Same in-progress-draft-first pattern as quantity, for the Selling Price
  // field - Total Amount updates instantly while typing, not just on blur.
  const getDisplaySellingPrice = (item) => {
    const draft = sellingPriceDrafts[item.id];
    if (draft === undefined) return Number(item.effective_selling_price ?? item.unit_price);
    const parsed = parseFloat(draft);
    return Number.isNaN(parsed) ? Number(item.effective_selling_price ?? item.unit_price) : parsed;
  };

  const getDisplayTotalAmount = (item) => getDisplaySellingPrice(item) * getDisplayQuantity(item);

  // The grand "Total" footer reflects Total Amount (what the customer is
  // actually being charged), not Our Price's cost basis.
  const getDisplayTotal = () => {
    if (!quotation) return 0;
    return quotation.line_items.reduce((sum, item) => sum + getDisplayTotalAmount(item), 0);
  };

  const handleQuantityCommit = async (itemId) => {
    if (!(itemId in quantityDrafts)) return;
    const quantity = Math.max(1, parseInt(quantityDrafts[itemId], 10) || 1);
    try {
      const updated = await updateQuotationLineItem(quotation.id, itemId, { quantity });
      setQuotation(updated);
    } catch (error) {
      setSnackbar({ open: true, message: 'Could not update quantity.', severity: 'error' });
    } finally {
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const handleSellingPriceChange = (itemId, value) => {
    setSellingPriceDrafts((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSellingPriceCommit = async (itemId) => {
    if (!(itemId in sellingPriceDrafts)) return;
    const parsed = parseFloat(sellingPriceDrafts[itemId]);
    const sellingPrice = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
    try {
      const updated = await updateQuotationLineItem(quotation.id, itemId, { selling_price: sellingPrice });
      setQuotation(updated);
    } catch (error) {
      setSnackbar({ open: true, message: 'Could not update selling price.', severity: 'error' });
    } finally {
      setSellingPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  // Switching category re-snapshots Our Price AND Selling Price server-side
  // to the new category's current price (see QuotationCurrentItemDetailView) -
  // a clean slate rather than carrying over a selling price that was set
  // against a different category's cost basis.
  const performPriceCategoryChange = async (itemId, priceCategory) => {
    setChangingCategoryItemId(itemId);
    try {
      const updated = await updateQuotationLineItem(quotation.id, itemId, { price_category: priceCategory });
      setQuotation(updated);
    } catch (error) {
      const message = error.response?.data?.error || 'Could not change price category.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setChangingCategoryItemId(null);
    }
  };

  // Gate on the chosen category's price being expired - if so, confirm via
  // modal (expiredPriceConfirm) instead of calling the API immediately.
  const handlePriceCategoryChange = (itemId, priceCategory) => {
    const item = quotation.line_items.find((li) => li.id === itemId);
    const option = skuPriceOptionsMap[item?.sku]?.find((o) => o.price_category === priceCategory);
    if (option?.isExpired) {
      setExpiredPriceConfirm({ kind: 'switch', itemId, option });
      return;
    }
    performPriceCategoryChange(itemId, priceCategory);
  };

  // Whether this line's frozen Our Price (unit_price) is out of date against
  // the latest known price for its category (from skuPriceOptionsMap,
  // fetched for every quotation, not just drafts - see the effect above).
  // Shows the latest price even when it's expired (isExpired) rather than
  // "N/A" - that's still the real, current answer to "what would this cost
  // today," just under a lapsed category. No price on record at all is the
  // only case with nothing to show/update.
  const getPriceChangeInfo = (item) => {
    const option = skuPriceOptionsMap[item.sku]?.find((o) => o.price_category === item.price_category);
    const hasCurrentPrice = option !== undefined;
    const hasChanged = hasCurrentPrice && Number(option.unit_price) !== Number(item.unit_price);
    return { hasCurrentPrice, hasChanged, currentPrice: option?.unit_price, isExpired: option?.isExpired };
  };

  const changedPriceCount = quotation
    ? quotation.line_items.filter((item) => getPriceChangeInfo(item).hasChanged).length
    : 0;

  // Separate from changedPriceCount above - a line can be quoted at a price
  // that's since expired even if the dollar amount itself hasn't changed
  // (e.g. the category just hasn't been renewed yet), so this needs its own
  // count/banner rather than folding into the price-change one.
  const expiredPriceCount = quotation
    ? quotation.line_items.filter((item) => item.is_price_expired).length
    : 0;

  const handleRefreshLineItemPrice = async (itemId) => {
    setRefreshingItemId(itemId);
    try {
      const updated = await refreshQuotationLineItemPrice(quotation.id, itemId);
      setQuotation(updated);
      setSnackbar({ open: true, message: 'Price updated.', severity: 'success' });
    } catch (error) {
      const message = error.response?.data?.error || 'Could not update price.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setRefreshingItemId(null);
    }
  };

  const handleRefreshAllPrices = async () => {
    setRefreshingAllPrices(true);
    try {
      const { updated_count: updatedCount, quotation: updated } = await refreshQuotationPrices(quotation.id);
      setQuotation(updated);
      setSnackbar({
        open: true,
        message: updatedCount
          ? `Updated pricing on ${updatedCount} item${updatedCount === 1 ? '' : 's'}.`
          : 'All prices are already up to date.',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Could not update prices.', severity: 'error' });
    } finally {
      setRefreshingAllPrices(false);
    }
  };

  // Debounced product search for the Add Item autocomplete.
  useEffect(() => {
    if (!addItemQuery || addItemQuery.length < 2) {
      setAddItemOptions([]);
      return;
    }
    let cancelled = false;
    setAddItemSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchProducts(addItemQuery);
        if (!cancelled) setAddItemOptions(results);
      } catch (error) {
        if (!cancelled) setAddItemOptions([]);
      } finally {
        if (!cancelled) setAddItemSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [addItemQuery]);

  const handleAddItemProductChange = async (product) => {
    setAddItemProduct(product);
    setAddItemPriceOptions([]);
    setAddItemPriceCategory('');
    if (!product) return;
    try {
      const history = await getAllPriceHistoryForSku(product.item_number);
      const options = reduceToLatestPerCategory(history);
      setAddItemPriceOptions(options);
      // Default to the first non-expired category if there is one; only
      // fall back to an expired one (still better than leaving it blank).
      const defaultOption = options.find((o) => !o.isExpired) || options[0];
      if (defaultOption) setAddItemPriceCategory(defaultOption.price_category);
    } catch (error) {
      // No pricing on record for this SKU - leave the category picker empty;
      // the Add button stays disabled until one is available.
    }
  };

  const performAddItem = async () => {
    setAddingItem(true);
    try {
      const updated = await addQuotationLineItem(quotation.id, {
        sku: addItemProduct.item_number,
        priceCategory: addItemPriceCategory,
        quantity: Math.max(1, parseInt(addItemQuantity, 10) || 1),
      });
      setQuotation(updated);
      setSnackbar({ open: true, message: 'Item added.', severity: 'success' });
      setAddItemProduct(null);
      setAddItemPriceOptions([]);
      setAddItemPriceCategory('');
      setAddItemQuantity(1);
      setAddItemQuery('');
      setAddItemOptions([]);
      setAddItemResetKey((prev) => prev + 1);
    } catch (error) {
      const message = error.response?.data?.error || 'Could not add item.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setAddingItem(false);
    }
  };

  // Gate on the chosen category's price being expired, same as changing
  // category on an existing line.
  const handleAddItem = () => {
    if (!addItemProduct || !addItemPriceCategory) return;
    const option = addItemPriceOptions.find((o) => o.price_category === addItemPriceCategory);
    if (option?.isExpired) {
      setExpiredPriceConfirm({ kind: 'add', option });
      return;
    }
    performAddItem();
  };

  const handleConfirmExpiredPrice = () => {
    if (!expiredPriceConfirm) return;
    if (expiredPriceConfirm.kind === 'add') {
      performAddItem();
    } else {
      performPriceCategoryChange(expiredPriceConfirm.itemId, expiredPriceConfirm.option.price_category);
    }
    setExpiredPriceConfirm(null);
  };

  const handleRemove = async (itemId) => {
    setRemovingItemId(itemId);
    try {
      const updated = await removeQuotationLineItem(quotation.id, itemId);
      setQuotation(updated);
      setSnackbar({ open: true, message: 'Item removed.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Could not remove item.', severity: 'error' });
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await saveQuotation(quotationId, quotationName);
      setQuotation(updated);
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Quotation saved.', severity: 'success' });
    } catch (error) {
      const message = error?.response?.data?.error || 'Could not save this quotation.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!quotation) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">Quotation not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" onClick={() => navigate('/quotations')} sx={{ cursor: 'pointer' }}>
          Quotations
        </Link>
        <Typography color="text.primary">{quotation.name || `Quotation #${quotation.id}`}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {quotation.name || `Quotation #${quotation.id}`}
          </Typography>
          <Chip
            size="small"
            label={isDraft ? 'Draft' : 'Saved'}
            color={isDraft ? 'warning' : 'success'}
            sx={{ mt: 1 }}
          />
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isDraft && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={() => navigate('/products/list')}
              sx={{ textTransform: 'none' }}
            >
              Browse More Products
            </Button>
          )}
          {quotation.line_items.length > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={exportingFormat === 'excel' ? <CircularProgress size={14} /> : <ExcelIcon />}
                onClick={() => handleOpenColumnsDialog('excel')}
                disabled={!!exportingFormat}
                sx={{ textTransform: 'none' }}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={exportingFormat === 'pdf' ? <CircularProgress size={14} /> : <PdfIcon />}
                onClick={() => handleExport('pdf', null)}
                disabled={!!exportingFormat}
                sx={{ textTransform: 'none' }}
              >
                Sales Sheet
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={exportingFormat === 'pptx' ? <CircularProgress size={14} /> : <SlideshowIcon />}
                endIcon={<AutoAwesomeIcon fontSize="small" sx={{ color: '#F9A91E' }} />}
                onClick={() => handleOpenColumnsDialog('pptx')}
                disabled={!!exportingFormat}
                sx={{ textTransform: 'none' }}
              >
                PowerPoint
              </Button>
              {isDraft && (
                <Button variant="contained" startIcon={<SaveIcon />} onClick={() => setSaveDialogOpen(true)}>
                  Save Quotation
                </Button>
              )}
            </>
          )}
        </Stack>
      </Box>

      {!isDraft && changedPriceCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={refreshingAllPrices ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              onClick={handleRefreshAllPrices}
              disabled={refreshingAllPrices}
            >
              Update All
            </Button>
          }
        >
          {changedPriceCount} item{changedPriceCount === 1 ? '' : 's'} on this quotation{' '}
          {changedPriceCount === 1 ? 'has' : 'have'} updated pricing since it was saved.
        </Alert>
      )}

      {expiredPriceCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {expiredPriceCount} item{expiredPriceCount === 1 ? '' : 's'} on this quotation{' '}
          {expiredPriceCount === 1 ? 'is' : 'are'} priced under an expired price category - double-check
          with the customer before finalizing.
        </Alert>
      )}

      {quotation.line_items.length === 0 ? (
        <Alert severity="info">
          This {isDraft ? 'basket is' : 'quotation is'} empty.{' '}
          <Link onClick={() => navigate('/products/list')} sx={{ cursor: 'pointer' }}>
            Browse products
          </Link>{' '}
          to add items.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">On Hand</TableCell>
                <TableCell>Price Category</TableCell>
                <TableCell align="right">Our Price</TableCell>
                {!isDraft && <TableCell align="right">Price Now</TableCell>}
                {!isDraft && <TableCell align="center">Update Price</TableCell>}
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Selling Price</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotation.line_items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box
                      component="img"
                      src={item.thumbnail_url || noImage}
                      alt={item.sku}
                      onClick={() => setImageModalUrl(item.full_image_url || item.thumbnail_url || noImage)}
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: 'grey.50',
                        cursor: 'pointer',
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>{item.product_description || '-'}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ color: !item.on_hand ? 'error.main' : 'text.primary' }}
                    >
                      {item.on_hand ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    {/* Price category (and the other line fields below) are editable
                        whether this quotation is a draft or already saved - saved
                        quotations are no longer read-only, see the full-edit feature. */}
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Select
                        size="small"
                        fullWidth
                        value={item.price_category}
                        onChange={(e) => handlePriceCategoryChange(item.id, e.target.value)}
                        disabled={changingCategoryItemId === item.id}
                      >
                        {/* Every category this SKU has ever had a price for is offered,
                            expired ones included (flagged in red) - falls back to just
                            the line's own category if options haven't loaded yet, so
                            the Select always has a valid value. */}
                        {(skuPriceOptionsMap[item.sku]?.length
                          ? skuPriceOptionsMap[item.sku]
                          : [{ price_category: item.price_category, price_category_display: item.price_category_display }]
                        ).map((option) => (
                          <MenuItem key={option.price_category} value={option.price_category}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{option.price_category_display}</span>
                              {option.isExpired && (
                                <Chip size="small" color="error" label={`Expired ${option.effective_end_date}`} />
                              )}
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                      {changingCategoryItemId === item.id && <CircularProgress size={16} />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">${Number(item.unit_price).toFixed(2)}</TableCell>
                  {!isDraft && (() => {
                    const { hasCurrentPrice, hasChanged, currentPrice } = getPriceChangeInfo(item);
                    const diff = hasCurrentPrice ? Number(currentPrice) - Number(item.unit_price) : 0;
                    const diffLabel = diff === 0
                      ? 'No change'
                      : `${diff > 0 ? '+' : ''}$${diff.toFixed(2)}`;
                    const diffColor = diff > 0 ? 'error.main' : diff < 0 ? 'success.main' : 'text.secondary';
                    return (
                      <>
                        <TableCell align="right">
                          {hasCurrentPrice ? (
                            <Box>
                              <Typography variant="body2">${Number(currentPrice).toFixed(2)}</Typography>
                              <Typography variant="caption" sx={{ color: diffColor, display: 'block' }}>
                                {diffLabel}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {hasChanged && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                refreshingItemId === item.id
                                  ? <CircularProgress size={14} />
                                  : <SyncIcon fontSize="small" />
                              }
                              onClick={() => handleRefreshLineItemPrice(item.id)}
                              disabled={refreshingItemId === item.id}
                              sx={{ textTransform: 'none' }}
                            >
                              Update
                            </Button>
                          )}
                        </TableCell>
                      </>
                    );
                  })()}
                  <TableCell align="right" sx={{ width: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={quantityDrafts[item.id] ?? item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      onBlur={() => handleQuantityCommit(item.id)}
                      inputProps={{ min: 1, style: { textAlign: 'right' } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={sellingPriceDrafts[item.id] ?? item.effective_selling_price ?? item.unit_price}
                      onChange={(e) => handleSellingPriceChange(item.id, e.target.value)}
                      onBlur={() => handleSellingPriceCommit(item.id)}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right">${getDisplayTotalAmount(item).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(item.id)}
                      disabled={removingItemId === item.id}
                    >
                      {removingItemId === item.id
                        ? <CircularProgress size={16} />
                        : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Add Item</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
          <Autocomplete
            key={addItemResetKey}
            sx={{ flex: 2, minWidth: 220 }}
            size="small"
            options={addItemOptions}
            value={addItemProduct}
            // inputValue is deliberately NOT controlled here. Selecting an
            // option fires onInputChange too (reason 'reset'), with the
            // option's own label as the value - if that fed back into
            // addItemQuery, it would immediately re-trigger the debounced
            // search with "SKU - Product Name" as the query, breaking the
            // next search. Only real typing (reason 'input') should drive
            // the search; MUI manages the visible input text itself.
            onInputChange={(_e, value, reason) => {
              if (reason === 'input') setAddItemQuery(value);
            }}
            onChange={(_e, value) => handleAddItemProductChange(value)}
            getOptionLabel={(option) => (option ? `${option.item_number} - ${option.product_name}` : '')}
            isOptionEqualToValue={(option, value) => option.item_number === value.item_number}
            loading={addItemSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search product by name or SKU"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {addItemSearching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Select
            size="small"
            sx={{ minWidth: 190 }}
            displayEmpty
            value={addItemPriceCategory}
            onChange={(e) => setAddItemPriceCategory(e.target.value)}
            disabled={!addItemProduct || addItemPriceOptions.length === 0}
          >
            <MenuItem value="" disabled>
              {addItemProduct && addItemPriceOptions.length === 0 ? 'No price on record' : 'Price category'}
            </MenuItem>
            {addItemPriceOptions.map((option) => (
              <MenuItem key={option.price_category} value={option.price_category}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{option.price_category_display}</span>
                  {option.isExpired && (
                    <Chip size="small" color="error" label={`Expired ${option.effective_end_date}`} />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            type="number"
            label="Qty"
            value={addItemQuantity}
            onChange={(e) => setAddItemQuantity(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ width: 90 }}
          />
          <Button
            variant="contained"
            startIcon={addingItem ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            onClick={handleAddItem}
            disabled={!addItemProduct || !addItemPriceCategory || addingItem}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      {quotation.line_items.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Typography variant="h6">Total: ${getDisplayTotal().toFixed(2)}</Typography>
        </Box>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Quotation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give this quotation a name (optional) and save it to your history.
          </DialogContentText>
          <TextField
            fullWidth
            label="Quotation Name"
            value={quotationName}
            onChange={(e) => setQuotationName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!expiredPriceConfirm} onClose={() => setExpiredPriceConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Price Expired</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The {expiredPriceConfirm?.option?.price_category_display} price for this item expired on{' '}
            {expiredPriceConfirm?.option?.effective_end_date}. Are you sure you want to{' '}
            {expiredPriceConfirm?.kind === 'add' ? 'add it' : 'use it'} anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiredPriceConfirm(null)}>Cancel</Button>
          <Button onClick={handleConfirmExpiredPrice} variant="contained" color="warning">
            Yes, Use It
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {pendingExportFormat === 'pptx' && <AutoAwesomeIcon sx={{ color: '#F9A91E' }} />}
          {pendingExportFormat === 'pptx' ? 'Design Your PowerPoint' : 'Choose Export Columns'}
          {pendingExportFormat ? ` - ${EXPORT_FORMAT_LABELS[pendingExportFormat]}` : ''}
        </DialogTitle>
        <DialogContent>
          {pendingExportFormat === 'pptx' ? (
            <>
              <DialogContentText sx={{ mb: 1 }}>
                One slide per product. Check which details to show on each slide, and drag the handle to
                reorder them. The Gibson logo is always included as a footer watermark.
              </DialogContentText>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" onClick={handleSelectAllPptxFields}>Select All</Button>
                <Button size="small" onClick={handleUnselectAllPptxFields}>Unselect All</Button>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 1, mb: 2 }}>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handlePptxFieldDragEnd}>
                  <SortableContext items={pptxFieldOrder} strategy={verticalListSortingStrategy}>
                    {pptxFieldOrder.map((key) => (
                      <SortableColumnRow
                        key={key}
                        id={key}
                        label={pptxFieldLabelByKey[key] || key}
                        checked={selectedPptxFields.has(key)}
                        onToggle={() => handleTogglePptxField(key)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={pptxUseDefaultTemplate}
                    onChange={(e) => setPptxUseDefaultTemplate(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#F9A91E' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F9A91E' },
                    }}
                  />
                }
                label="Use the plain white default template"
                sx={{ mb: 1 }}
              />
              {pptxUseDefaultTemplate ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your PowerPoint will use the plain white template.
                </Typography>
              ) : (
                <>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Instructions for AI (optional)"
                    placeholder='e.g. "elegant and modern, with a full frame border"'
                    value={pptxDesignDescription}
                    onChange={(e) => setPptxDesignDescription(e.target.value)}
                    helperText="Describe the layout and feel you want (font, borders/bars, seasonal touches) and AI will design a matching layout, always using Gibson Homewares' colors. Leave blank for a plain white design. If AI generation fails for any reason, the plain design is used automatically."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <AutoAwesomeIcon fontSize="small" sx={{ color: '#F9A91E' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#F9A91E', borderWidth: 2 },
                        '&:hover fieldset': { borderColor: '#C17D00' },
                        '&.Mui-focused fieldset': { borderColor: '#C17D00' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C17D00' },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                    Need inspiration? Try one of these:
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {PPTX_PROMPT_EXAMPLES.map((example) => (
                      <Chip
                        key={example.label}
                        label={example.label}
                        size="small"
                        variant="outlined"
                        icon={<AutoAwesomeIcon fontSize="small" />}
                        onClick={() => setPptxDesignDescription(example.text)}
                        sx={{
                          borderColor: '#F9A91E',
                          color: '#8A5A00',
                          '& .MuiChip-icon': { color: '#F9A91E' },
                          '&:hover': { backgroundColor: '#FFF3D6' },
                        }}
                      />
                    ))}
                  </Stack>
                </>
              )}
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 1 }}>
                Check which columns to include, and drag the handle to reorder them.
              </DialogContentText>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" onClick={handleSelectAllColumns}>Select All</Button>
                <Button size="small" onClick={handleUnselectAllColumns}>Unselect All</Button>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
                  <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
                    {columnOrder.map((key) => (
                      <SortableColumnRow
                        key={key}
                        id={key}
                        label={columnLabelByKey[key] || key}
                        checked={selectedColumns.has(key)}
                        onToggle={() => handleToggleColumn(key)}
                        forced={key === 'sku'}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmExport} variant="contained">
            Export to {pendingExportFormat ? EXPORT_FORMAT_LABELS[pendingExportFormat] : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={salesSheetBuildingOpen} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {/* A mock Sales Sheet page that continuously builds itself up and
              resets in a loop (orange banner, title, description lines,
              product photo) - a stand-in for the real page(s) being
              assembled server-side, since fetching each line's product
              photo/brand logo can take a while. */}
          <Box
            sx={{
              position: 'relative',
              width: 150,
              height: 190,
              mx: 'auto',
              mb: 3,
              borderRadius: 2,
              bgcolor: '#fff',
              border: '1px solid #eee',
              overflow: 'hidden',
              animation: 'salesSheetGlow 4.5s ease-in-out infinite',
              '@keyframes salesSheetGlow': {
                '0%, 60%, 100%': { boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
                '68%, 82%': { boxShadow: '0 0 0 3px #F5A623, 0 6px 20px rgba(0,0,0,0.1)' },
              },
            }}
          >
            {/* orange banner, matches the real Sales Sheet header */}
            <Box
              sx={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 18,
                bgcolor: '#F5A623',
                transformOrigin: 'left',
                animation: 'salesSheetBanner 4.5s ease-in-out infinite',
                '@keyframes salesSheetBanner': {
                  '0%, 100%': { transform: 'scaleX(0)' },
                  '8%, 90%': { transform: 'scaleX(1)' },
                },
              }}
            />
            {/* title line */}
            <Box
              sx={{
                position: 'absolute', top: 28, left: 20, right: 20, height: 8, borderRadius: 4,
                bgcolor: '#1A1A1A',
                animation: 'salesSheetTitle 4.5s ease-in-out infinite',
                '@keyframes salesSheetTitle': {
                  '0%, 18%, 90%, 100%': { opacity: 0, width: 0 },
                  '28%, 80%': { opacity: 1, width: '70%' },
                },
              }}
            />
            {/* description lines, typing in one after another */}
            {[
              { top: 44, delayPct: 32, widthPct: 80 },
              { top: 54, delayPct: 36, widthPct: 60 },
            ].map((line, i) => (
              <Box
                key={line.top}
                sx={{
                  position: 'absolute', top: line.top, left: 26, height: 6, borderRadius: 3,
                  bgcolor: '#1a9dc3', opacity: 0.6,
                  animation: `salesSheetDesc${i} 4.5s ease-in-out infinite`,
                  [`@keyframes salesSheetDesc${i}`]: {
                    [`0%, ${line.delayPct}%, 90%, 100%`]: { width: 0 },
                    [`${line.delayPct + 10}%, 80%`]: { width: `${line.widthPct}%` },
                  },
                }}
              />
            ))}
            {/* product photo placeholder, centered lower half */}
            <Box
              sx={{
                position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                width: 60, height: 60, borderRadius: 1,
                bgcolor: '#FFF3D6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'salesSheetImage 4.5s ease-in-out infinite',
                '@keyframes salesSheetImage': {
                  '0%, 45%, 92%, 100%': { opacity: 0, transform: 'translateX(-50%) scale(0.4)' },
                  '58%, 85%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
                },
              }}
            >
              <ImageIcon sx={{ color: '#F5A623', fontSize: 28 }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Building Your Sales Sheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 24, mb: 1 }}>
            {SALES_SHEET_BUILD_STEPS[salesSheetBuildStepIndex]}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            This can take a little while for quotations with several items - please be patient.
          </Typography>
          <LinearProgress
            sx={{
              mb: 3, height: 6, borderRadius: 3,
              '& .MuiLinearProgress-bar': { backgroundColor: '#F5A623' },
              backgroundColor: '#FFF3D6',
            }}
          />
          <Button variant="outlined" color="inherit" onClick={handleCancelSalesSheetBuild}>
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={pptxBuildingOpen} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {/* A mock slide canvas that continuously builds itself up and
              resets in a 5s loop (accent bar, photo, text lines, watermark,
              a glowing "done" moment, then it clears and starts again) - a
              perpetual stand-in for the real slide being assembled
              server-side, since the actual request can run far longer than
              one pass through PPTX_BUILD_STEPS. */}
          <Box
            sx={{
              position: 'relative',
              width: 220,
              height: 140,
              mx: 'auto',
              mb: 3,
              borderRadius: 2,
              bgcolor: '#fff',
              border: '1px solid #eee',
              overflow: 'hidden',
              animation: 'pptxCanvasGlow 5s ease-in-out infinite',
              '@keyframes pptxCanvasGlow': {
                '0%, 60%, 100%': { boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
                '68%, 82%': { boxShadow: '0 0 0 3px #F9A91E, 0 6px 20px rgba(0,0,0,0.1)' },
              },
            }}
          >
            {/* top accent bar - the AI "decides" the theme */}
            <Box
              sx={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 8,
                bgcolor: '#F9A91E',
                transformOrigin: 'left',
                animation: 'pptxBar 5s ease-in-out infinite',
                '@keyframes pptxBar': {
                  '0%, 100%': { transform: 'scaleX(0)' },
                  '10%, 85%': { transform: 'scaleX(1)' },
                  '95%': { transform: 'scaleX(0)' },
                },
              }}
            />
            {/* photo placeholder */}
            <Box
              sx={{
                position: 'absolute', top: 26, left: 16, width: 52, height: 52, borderRadius: 1,
                bgcolor: '#FFF3D6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'pptxImage 5s ease-in-out infinite',
                '@keyframes pptxImage': {
                  '0%, 18%, 90%, 100%': { opacity: 0, transform: 'scale(0.4)' },
                  '28%, 80%': { opacity: 1, transform: 'scale(1)' },
                },
              }}
            >
              <ImageIcon sx={{ color: '#F9A91E', fontSize: 26 }} />
            </Box>
            {/* text lines, typing in one after another */}
            {[
              { width: 76, color: '#1D5576', start: 30, grow: 40 },
              { width: 60, color: '#D9D9D9', start: 36, grow: 46 },
              { width: 46, color: '#D9D9D9', start: 42, grow: 52 },
            ].map((line, i) => (
              <Box
                key={line.width}
                sx={{
                  position: 'absolute', top: 32 + i * 16, left: 80, height: 8, borderRadius: 4,
                  bgcolor: line.color,
                  animation: `pptxLine${i} 5s ease-in-out infinite`,
                  [`@keyframes pptxLine${i}`]: {
                    [`0%, ${line.start}%, 90%, 100%`]: { width: 0 },
                    [`${line.grow}%, 80%`]: { width: `${line.width}px` },
                  },
                }}
              />
            ))}
            {/* Gibson watermark dot, added last */}
            <Box
              sx={{
                position: 'absolute', bottom: 12, right: 14, width: 18, height: 18, borderRadius: '50%',
                bgcolor: '#F9A91E',
                animation: 'pptxDot 5s ease-in-out infinite',
                '@keyframes pptxDot': {
                  '0%, 52%, 90%, 100%': { opacity: 0, transform: 'scale(0.3)' },
                  '62%, 80%': { opacity: 0.85, transform: 'scale(1)' },
                },
              }}
            />
            {/* pulsing AI sparkle badge */}
            <Box
              sx={{
                position: 'absolute', top: -12, right: -12, width: 32, height: 32, borderRadius: '50%',
                bgcolor: '#fff', border: '2px solid #F9A91E',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'pptxBuildPulse 1.6s ease-in-out infinite',
                '@keyframes pptxBuildPulse': {
                  '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
                  '50%': { transform: 'scale(1.15) rotate(15deg)' },
                },
              }}
            >
              <AutoAwesomeIcon sx={{ color: '#F9A91E', fontSize: 18 }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Gibson AI is Building Your Presentation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 24, mb: 3 }}>
            {PPTX_BUILD_STEPS[pptxBuildStepIndex]}
          </Typography>
          <LinearProgress
            sx={{
              mb: 3, height: 6, borderRadius: 3,
              '& .MuiLinearProgress-bar': { backgroundColor: '#F9A91E' },
              backgroundColor: '#FFF3D6',
            }}
          />
          <Button variant="outlined" color="inherit" onClick={handleCancelPptxBuild}>
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!imageModalUrl} onClose={() => setImageModalUrl(null)} maxWidth="md">
        <Box
          component="img"
          src={imageModalUrl}
          alt="Product"
          sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
        />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuotationDetailPage;
